import { SystemSlotEvent } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { getBrowserDetails, getViewportDimensions, once, throttle } from '@mbrtargeting/metatag-utils';
import { triggerEvent } from '../../loader/essentials/events.js';
import { logger } from '../../loader/essentials/logger.js';
import { Adslot } from '../modules/adslot-controller/adslot.js';
import { EffectFunction } from './index.js';

const log = logger.logGroup({ prefix: 'LazyLoad Effect' });

let lastPos: number = 0;
let scrollDelta: number = 0;

interface ObserverConfig {
    observerName: string;
    factor: number;
    minScrollDelta: number;
    maxScrollDelta: number;
}

addEventListener('scroll', throttle(() => {
    const newPos: number = window.scrollY;
    const delta: number = newPos - lastPos;
    lastPos = newPos;
    scrollDelta = Math.abs(delta);
}, 10));

const createObserverConfigs = (): ObserverConfig[] => {
    const { isTablet, isPhone } = getBrowserDetails();
    if (isTablet) return [
        { observerName: 'FastScrollMobileFactor0.5', factor: 0.4, minScrollDelta: 401, maxScrollDelta: Number.MAX_SAFE_INTEGER },
        // { observerName: 'FastScrollMobileFactor0.3', factor: 0.3, minScrollDelta: 301, maxScrollDelta: 400 },
        // { observerName: 'FastScrollMobileFactor0.2', factor: 0.2, minScrollDelta: 201, maxScrollDelta: 300 },
        // { observerName: 'FastScrollMobileFactor0.1', factor: 0.1, minScrollDelta: 101, maxScrollDelta: 200 },
        { observerName: 'StandardScrollMobile', factor: 0, minScrollDelta: 0, maxScrollDelta: 400 },
    ];
    if (isPhone) [
        { observerName: 'FastScrollMobileFactor0.5', factor: 0.5, minScrollDelta: 501, maxScrollDelta: Number.MAX_SAFE_INTEGER },
        // { observerName: 'FastScrollMobileFactor0.4', factor: 0.4, minScrollDelta: 401, maxScrollDelta: 500 },
        // { observerName: 'FastScrollMobileFactor0.3', factor: 0.3, minScrollDelta: 301, maxScrollDelta: 400 },
        // { observerName: 'FastScrollMobileFactor0.2', factor: 0.2, minScrollDelta: 201, maxScrollDelta: 300 },
        // { observerName: 'FastScrollMobileFactor0.1', factor: 0.1, minScrollDelta: 101, maxScrollDelta: 200 },
        { observerName: 'StandardScrollMobile', factor: 0, minScrollDelta: 0, maxScrollDelta: 500 },
    ];
    return [
        { observerName: 'VeryFastScroll', factor: 0.2, minScrollDelta: 301, maxScrollDelta: Number.MAX_SAFE_INTEGER },
        // { observerName: 'fastScroll', factor: 0.1, minScrollDelta: 301, maxScrollDelta: 500 },
        { observerName: 'StandardScroll', factor: 0, minScrollDelta: 0, maxScrollDelta: 300 },
    ];
}

export const LAZYLOAD_SLOT: EffectFunction = (adslot: Adslot) => {

    const { viewportHeight } = getViewportDimensions();
    const observerConfigs = createObserverConfigs();

    const sendLazyLoadEventOnce = once(observerName => {
        log.debug(`lazyLoad done event triggered from observer ${observerName} with scrollDelta ${scrollDelta} on the Slot ${adslot.slotName}`);
        triggerEvent(SystemSlotEvent.SDG_SLOT_LAZY_LOAD_READY, {
            passedObject: adslot,
            placement: adslot,
            slot: adslot.slotName,
            position: adslot.slotName,
        });
    });

    const createObserverCallback = ({ minScrollDelta, maxScrollDelta, observerName }: ObserverConfig) => (entries: IntersectionObserverEntry[]) => {
        const isIntersecting = entries.some(entry => entry.isIntersecting);
        if (isIntersecting && (minScrollDelta <= scrollDelta) && (scrollDelta <= maxScrollDelta)) {
            sendLazyLoadEventOnce(observerName);
        }
    };

    const observers = observerConfigs.map(observerConfig => {
        const observer = new IntersectionObserver(createObserverCallback(observerConfig), {
            rootMargin: `${viewportHeight * observerConfig.factor}px`,
            threshold: 0
        });
        observer.observe(adslot.containerNode);
        return observer;
    });

    return () => {
        for (const observer of observers) {
            observer.unobserve(adslot.containerNode);
        }
    };
};
